
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { T } from '@/components/T';

// Reduced and optimized testimonials data
const testimonials = [
  {
    id: 1,
    quote: "Finding a therapist who really understood my needs was a game-changer. The platform made it so easy to connect with qualified therapists, psychologists, and psychotherapists who specialize in my specific challenges.",
    author: "Sarah J.",
    location: "New York, USA",
  },
  {
    id: 2,
    quote: "As someone who travels frequently, being able to maintain consistent therapy sessions online with my therapist has been invaluable. The platform connects me with psychologists and psychotherapists no matter where I am in the world.",
    author: "Michael T.",
    location: "London, UK",
  },
  {
    id: 3,
    quote: "The booking process is incredibly smooth, and I appreciate being able to see therapists', psychologists', and psychotherapists' specialties and approaches before making a decision. It helped me find the perfect match.",
    author: "Elena R.",
    location: "Madrid, Spain",
  },
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  
  useEffect(() => {
    // Auto-rotate testimonials every 6 seconds if autoplay is enabled
    if (autoplayEnabled) {
      const intervalId = setInterval(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 6000);
      
      return () => clearInterval(intervalId);
    }
  }, [autoplayEnabled]);
  
  const nextTestimonial = () => {
    setAutoplayEnabled(false); // Disable autoplay when user manually navigates
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setAutoplayEnabled(false); // Disable autoplay when user manually navigates
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };
  
  const handleManualSelection = (index: number) => {
    setAutoplayEnabled(false);
    setActiveIndex(index);
  };
  
  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-brand-blue-50 to-brand-gray-50 overflow-hidden">
      <div className="container mx-auto px-4 relative">
        {/* Reduced decorative elements for better performance */}
        <div className="absolute top-0 left-0 w-40 h-40 bg-brand-blue-100/40 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-gray-900 mb-3">
            <T>What Our Clients Say</T>
          </h2>
          <p className="text-lg text-brand-gray-600 max-w-2xl mx-auto">
            <T>Real stories from people who found the right therapist, psychologist, or psychotherapist for their journey.</T>
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto relative">
          {/* Testimonials */}
          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-brand-gray-100 overflow-hidden">
            <div className="absolute top-0 right-0 text-brand-blue-100 opacity-20 transform translate-x-1/4 -translate-y-1/4">
              <Quote size={120} />
            </div>
            
            <div className="relative z-10">
              <div className="mb-6 text-xl md:text-2xl text-brand-gray-800 italic leading-relaxed">
                "<T>{testimonials[activeIndex].quote}</T>"
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-brand-gray-200 overflow-hidden mr-4">
                  {/* Simplified author initial display instead of image */}
                  <div className="w-full h-full bg-brand-blue-200 flex items-center justify-center">
                    <span className="text-brand-blue-600 font-bold">
                      {testimonials[activeIndex].author.charAt(0)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="font-semibold text-brand-gray-900">
                    <T>{testimonials[activeIndex].author}</T>
                  </p>
                  <p className="text-sm text-brand-gray-600">
                    <T>{testimonials[activeIndex].location}</T>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation Dots */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, index) => (
              <Button
                key={index}
                variant="ghost"
                size="icon"
                className={cn(
                  "w-3 h-3 rounded-full p-0 border",
                  index === activeIndex 
                    ? "bg-brand-blue-500 border-brand-blue-500" 
                    : "bg-brand-gray-200 border-brand-gray-300 hover:bg-brand-gray-300"
                )}
                onClick={() => handleManualSelection(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          
          {/* Arrow Controls - Only on larger screens */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-12 rounded-full bg-white shadow-md border border-brand-gray-100 hidden md:flex hover:bg-brand-gray-50"
            onClick={prevTestimonial}
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-12 rounded-full bg-white shadow-md border border-brand-gray-100 hidden md:flex hover:bg-brand-gray-50"
            onClick={nextTestimonial}
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
